import { apiClient, toServiceError } from '@/services/http/client'
import type {
  CaseReviewState,
  CaseWorkState,
  ApplicabilityAnswerCode,
  ApplicabilityState,
  PreparationLifecycle,
  RecommendedItr,
  ReturnCaseList,
  ReturnCaseWorkspace,
  ReturnPreparationCase,
  CaseParticipant,
  CaseParticipantAction,
  CaseParticipantKind,
  FactSourceType,
  FactType,
  FactVersion,
  EvidenceLocation,
  EvidenceRail,
  SalaryComponentInput,
  SalaryEmployerInput,
  SalaryEmploymentInput,
  SalaryHraInput,
  SalaryLtaInput,
  SalaryModuleReadModel,
  SalaryReliefInput,
  SalaryResourceResponse,
  CommonLedgerReadModel,
  CommonLedgerResourceResponse,
  CollaborationMutation,
  CollaborationWorkspace,
  AttestationMutation,
  SectionAttestationState,
  ExtractionCandidateMutation,
  ExtractionCandidateReviewList,
  OfficialImportResponse,
  OfficialSourceFamily,
  ReconciliationComparisonKind,
  ReconciliationList,
  ReconciliationMutation,
  ReconciliationRun,
  BrokerImportRowPage,
  CapitalResourceResponse,
  CapitalWorkspace,
  BusinessResourceResponse,
  BusinessWorkspace,
  InternationalResourceResponse,
  InternationalWorkspace,
  ComputationRun,
  RegimeComparison,
  FinalDeclarationSummary,
  ReadinessEvaluation,
  ReviewAssignment,
  ReviewDecision,
  ReviewException,
  ReviewSnapshot,
  ReviewWorkspace,
} from '@/types/returnCase.types'

async function safe<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation()
  } catch (error) {
    throw toServiceError(error)
  }
}

export interface ReturnCaseFilters {
  page?: number
  pageSize?: number
  customerId?: string
  lifecycleState?: PreparationLifecycle
  workState?: CaseWorkState
  reviewState?: CaseReviewState
}

export const returnCasesHttp = {
  list: (filters: ReturnCaseFilters = {}) =>
    safe(
      async () =>
        (
          await apiClient.get<ReturnCaseList>('/return-preparation-cases', {
            params: {
              page: filters.page ?? 1,
              page_size: filters.pageSize ?? 50,
              customer_id: filters.customerId || undefined,
              lifecycle_state: filters.lifecycleState || undefined,
              work_state: filters.workState || undefined,
              review_state: filters.reviewState || undefined,
            },
          })
        ).data
    ),
  get: (caseId: string) =>
    safe(
      async () =>
        (await apiClient.get<ReturnPreparationCase>(`/return-preparation-cases/${caseId}`)).data
    ),
  workspace: (caseId: string) =>
    safe(
      async () =>
        (await apiClient.get<ReturnCaseWorkspace>(`/return-preparation-cases/${caseId}/workspace`))
          .data
    ),
  create: (input: {
    customer_id: string
    period_value: string
    period_basis?: 'assessment_year' | 'tax_year'
  }) =>
    safe(
      async () =>
        (
          await apiClient.post<ReturnPreparationCase>('/return-preparation-cases', input, {
            headers: { 'Idempotency-Key': crypto.randomUUID() },
          })
        ).data
    ),
  updateShell: (
    caseId: string,
    input: {
      expected_revision: number
      reason: string
      lifecycle_state?: PreparationLifecycle
      work_state?: CaseWorkState
      review_state?: CaseReviewState
    }
  ) =>
    safe(
      async () =>
        (await apiClient.patch<ReturnPreparationCase>(`/return-preparation-cases/${caseId}`, input))
          .data
    ),
  applicability: (caseId: string) =>
    safe(
      async () =>
        (
          await apiClient.get<ApplicabilityState>(
            `/return-preparation-cases/${caseId}/applicability`
          )
        ).data
    ),
  saveAnswer: (
    caseId: string,
    questionKey: string,
    input: {
      expected_revision: number
      answer_code: ApplicabilityAnswerCode
      detail: Record<string, unknown>
      source: string
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.put<ApplicabilityState>(
            `/return-preparation-cases/${caseId}/applicability/answers/${questionKey}`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  recomputeRouting: (caseId: string, expectedRevision: number) =>
    safe(
      async () =>
        (
          await apiClient.post(
            `/return-preparation-cases/${caseId}/routing:recompute`,
            { expected_revision: expectedRevision, reason: 'User requested routing refresh' },
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  overrideRouting: (
    caseId: string,
    input: {
      expected_revision: number
      to_itr: RecommendedItr
      reason: string
      citation_or_evidence: string
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post(`/return-preparation-cases/${caseId}/routing:override`, input, {
            headers: { 'Idempotency-Key': crypto.randomUUID() },
          })
        ).data
    ),
  participants: (caseId: string) =>
    safe(
      async () =>
        (await apiClient.get<CaseParticipant[]>(`/return-preparation-cases/${caseId}/participants`))
          .data
    ),
  issueParticipantInvite: (
    caseId: string,
    input: {
      intended_user_id: string
      intended_email: string
      participant_kind: CaseParticipantKind
      purpose: string
      actions: CaseParticipantAction[]
      representative_grant_id?: string
      expires_in_hours?: number
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<CaseParticipant>(
            `/return-preparation-cases/${caseId}/participants/invites`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  revokeParticipantInvite: (caseId: string, inviteId: string, reason: string) =>
    safe(
      async () =>
        (
          await apiClient.post<CaseParticipant>(
            `/return-preparation-cases/${caseId}/participants/invites/${inviteId}:revoke`,
            { reason }
          )
        ).data
    ),
  resendParticipantInvite: (caseId: string, inviteId: string) =>
    safe(
      async () =>
        (
          await apiClient.post<CaseParticipant>(
            `/return-preparation-cases/${caseId}/participants/invites/${inviteId}:resend`,
            {}
          )
        ).data
    ),
  createModule: (
    caseId: string,
    input: {
      expected_revision: number
      module_type: string
      instance_key: string
      label: string
      schema_version: string
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post(`/return-preparation-cases/${caseId}/modules`, input, {
            headers: { 'Idempotency-Key': crypto.randomUUID() },
          })
        ).data
    ),
  saveFact: (
    caseId: string,
    factKey: string,
    input: {
      expected_revision: number
      module_instance_id: string
      fact_type: FactType
      schema_version: string
      value: unknown
      source_type: FactSourceType
      source_reference: string
      source_metadata?: Record<string, unknown>
      reason: string
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.put(`/return-preparation-cases/${caseId}/facts/${factKey}`, input, {
            headers: { 'Idempotency-Key': crypto.randomUUID() },
          })
        ).data
    ),
  withdrawFact: (
    caseId: string,
    factId: string,
    input: {
      expected_revision: number
      source_type: FactSourceType
      source_reference: string
      reason: string
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post(
            `/return-preparation-cases/${caseId}/facts/${factId}:withdraw`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  factHistory: (caseId: string, factId: string) =>
    safe(
      async () =>
        (
          await apiClient.get<FactVersion[]>(
            `/return-preparation-cases/${caseId}/facts/${factId}/history`
          )
        ).data
    ),
  evidenceRail: (caseId: string) =>
    safe(
      async () =>
        (await apiClient.get<EvidenceRail>(`/return-preparation-cases/${caseId}/evidence-rail`))
          .data
    ),
  bindChecklistEvidence: (
    caseId: string,
    input: {
      expected_revision: number
      checklist_slot_id: string
      document_id: string
      document_version_id: string
      supersedes_binding_id?: string
      reason: string
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post(`/return-preparation-cases/${caseId}/checklist-bindings`, input, {
            headers: { 'Idempotency-Key': crypto.randomUUID() },
          })
        ).data
    ),
  linkFactEvidence: (
    caseId: string,
    factId: string,
    input: {
      expected_revision: number
      binding_id: string
      evidence_role: 'PRIMARY' | 'SUPPORTING' | 'CORROBORATING'
      location: EvidenceLocation
      supersedes_link_id?: string
      reason: string
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post(
            `/return-preparation-cases/${caseId}/facts/${factId}/evidence-links`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  unlinkFactEvidence: (caseId: string, linkId: string, expectedRevision: number, reason: string) =>
    safe(
      async () =>
        (
          await apiClient.post(
            `/return-preparation-cases/${caseId}/evidence-links/${linkId}:unlink`,
            { expected_revision: expectedRevision, reason },
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  salaryModule: (caseId: string) =>
    safe(
      async () =>
        (
          await apiClient.get<SalaryModuleReadModel>(
            `/return-preparation-cases/${caseId}/modules/salary`
          )
        ).data
    ),
  createSalaryEmployer: (caseId: string, input: SalaryEmployerInput) =>
    safe(
      async () =>
        (
          await apiClient.post<SalaryResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/salary/employers`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  createSalaryEmployment: (caseId: string, input: SalaryEmploymentInput) =>
    safe(
      async () =>
        (
          await apiClient.post<SalaryResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/salary/employments`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  createSalaryComponent: (caseId: string, input: SalaryComponentInput) =>
    safe(
      async () =>
        (
          await apiClient.post<SalaryResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/salary/components`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  createSalaryHraPeriod: (caseId: string, input: SalaryHraInput) =>
    safe(
      async () =>
        (
          await apiClient.post<SalaryResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/salary/hra-periods`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  createSalaryLtaJourney: (caseId: string, input: SalaryLtaInput) =>
    safe(
      async () =>
        (
          await apiClient.post<SalaryResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/salary/lta-journeys`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  createSalaryReliefItem: (caseId: string, input: SalaryReliefInput) =>
    safe(
      async () =>
        (
          await apiClient.post<SalaryResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/salary/relief-items`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  commonLedger: (caseId: string) =>
    safe(
      async () =>
        (
          await apiClient.get<CommonLedgerReadModel>(
            `/return-preparation-cases/${caseId}/modules/common-ledger`
          )
        ).data
    ),
  createCommonLedgerResource: (caseId: string, path: string, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await apiClient.post<CommonLedgerResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/${path}`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  disposeContinuityItem: (caseId: string, itemId: string, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await apiClient.post<CommonLedgerResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/continuity/items/${itemId}:dispose`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  updateCommonLedgerEvidence: (
    caseId: string,
    requirementKey: string,
    input: Record<string, unknown>
  ) =>
    safe(
      async () =>
        (
          await apiClient.put<CommonLedgerResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/common-ledger/evidence/${requirementKey}`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  collaboration: (caseId: string) =>
    safe(
      async () =>
        (
          await apiClient.get<CollaborationWorkspace>(
            `/return-preparation-cases/${caseId}/collaboration`
          )
        ).data
    ),
  createQuery: (caseId: string, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await apiClient.post<CollaborationMutation>(
            `/return-preparation-cases/${caseId}/queries`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  queryCommand: (
    caseId: string,
    queryId: string,
    command: string,
    input: Record<string, unknown>
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<CollaborationMutation>(
            `/return-preparation-cases/${caseId}/queries/${queryId}:${command}`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  addQueryMessage: (caseId: string, queryId: string, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await apiClient.post<CollaborationMutation>(
            `/return-preparation-cases/${caseId}/queries/${queryId}/messages`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  createTask: (caseId: string, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await apiClient.post<CollaborationMutation>(
            `/return-preparation-cases/${caseId}/tasks`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  taskCommand: (caseId: string, taskId: string, command: string, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await apiClient.post<CollaborationMutation>(
            `/return-preparation-cases/${caseId}/tasks/${taskId}:${command}`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  attestation: (caseId: string, sectionKey: string) =>
    safe(
      async () =>
        (
          await apiClient.get<SectionAttestationState>(
            `/return-preparation-cases/${caseId}/sections/${sectionKey}/attestation-state`
          )
        ).data
    ),
  verifySection: (caseId: string, sectionKey: string, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await apiClient.post<AttestationMutation>(
            `/return-preparation-cases/${caseId}/sections/${sectionKey}/verifications`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  extractionCandidates: (caseId: string) =>
    safe(
      async () =>
        (
          await apiClient.get<ExtractionCandidateReviewList>(
            `/return-preparation-cases/${caseId}/candidates`
          )
        ).data
    ),
  startOfficialImport: (
    caseId: string,
    input: {
      expected_revision: number
      binding_id: string
      module_instance_id: string
      source_family: OfficialSourceFamily
      reason: string
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<OfficialImportResponse>(
            `/return-preparation-cases/${caseId}/official-imports`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  acceptExtractionCandidate: (
    caseId: string,
    candidateId: string,
    input: { expected_revision: number; displayed_candidate_sha256: string; reason: string }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<ExtractionCandidateMutation>(
            `/return-preparation-cases/${caseId}/candidates/${candidateId}:accept`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  correctExtractionCandidate: (
    caseId: string,
    candidateId: string,
    input: {
      expected_revision: number
      displayed_candidate_sha256: string
      corrected_value: unknown
      correction_reason: string
      reason: string
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<ExtractionCandidateMutation>(
            `/return-preparation-cases/${caseId}/candidates/${candidateId}:correct`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  rejectExtractionCandidate: (
    caseId: string,
    candidateId: string,
    input: { expected_revision: number; displayed_candidate_sha256: string; reason: string }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<ExtractionCandidateMutation>(
            `/return-preparation-cases/${caseId}/candidates/${candidateId}:reject`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  reconciliations: (caseId: string) =>
    safe(
      async () =>
        (
          await apiClient.get<ReconciliationList>(
            `/return-preparation-cases/${caseId}/reconciliations`
          )
        ).data
    ),
  startReconciliation: (
    caseId: string,
    input: {
      expected_revision: number
      comparison_kind: ReconciliationComparisonKind
      source_candidate_ids: string[]
      reason: string
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<ReconciliationRun>(
            `/return-preparation-cases/${caseId}/reconciliations`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  annotateReconciliation: (
    caseId: string,
    itemId: string,
    input: { expected_revision: number; displayed_item_sha256: string; annotation: string }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<ReconciliationMutation>(
            `/return-preparation-cases/${caseId}/reconciliation-items/${itemId}:annotate`,
            input
          )
        ).data
    ),
  decideReconciliation: (
    caseId: string,
    itemId: string,
    input: {
      expected_revision: number
      displayed_item_sha256: string
      action:
        | 'ACCEPT_LOCAL'
        | 'REQUEST_SOURCE_CORRECTION'
        | 'ASK_CUSTOMER'
        | 'MARK_DUPLICATE'
        | 'CORRECT_LOCAL_SEPARATELY'
        | 'WAIVE_WITH_CA_REASON'
      reason_code: string
      narrative: string
      evidence_binding_id?: string
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<ReconciliationMutation>(
            `/return-preparation-cases/${caseId}/reconciliation-items/${itemId}:decide`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  capitalWorkspace: (caseId: string) =>
    safe(
      async () =>
        (
          await apiClient.get<CapitalWorkspace>(
            `/return-preparation-cases/${caseId}/modules/capital-gains`
          )
        ).data
    ),
  brokerImportRows: (caseId: string, batchId: string, page = 1, pageSize = 100) =>
    safe(
      async () =>
        (
          await apiClient.get<BrokerImportRowPage>(
            `/return-preparation-cases/${caseId}/modules/capital-gains/broker-imports/${batchId}/rows`,
            { params: { page, page_size: pageSize } }
          )
        ).data
    ),
  validateBrokerImport: (caseId: string, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await apiClient.post<CapitalResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/capital-gains/broker-imports`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  flagBrokerImportRow: (caseId: string, rowId: string, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await apiClient.post<CapitalResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/capital-gains/broker-import-rows/${rowId}/flags`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  businessWorkspace: (caseId: string) =>
    safe(
      async () =>
        (
          await apiClient.get<BusinessWorkspace>(
            `/return-preparation-cases/${caseId}/modules/business`
          )
        ).data
    ),
  createBusinessResource: (caseId: string, path: string, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await apiClient.post<BusinessResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/business/${path}`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  evaluateBusiness: (caseId: string, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await apiClient.post<BusinessResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/business:evaluate`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  internationalWorkspace: (caseId: string) =>
    safe(
      async () =>
        (
          await apiClient.get<InternationalWorkspace>(
            `/return-preparation-cases/${caseId}/modules/international`
          )
        ).data
    ),
  createInternationalResource: (caseId: string, path: string, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await apiClient.post<InternationalResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/international/${path}`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  evaluateInternational: (caseId: string, input: Record<string, unknown>) =>
    safe(
      async () =>
        (
          await apiClient.post<InternationalResourceResponse>(
            `/return-preparation-cases/${caseId}/modules/international:evaluate`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  runComputation: (caseId: string, expectedRevision: number, regime: 'OLD' | 'NEW') =>
    safe(
      async () =>
        (
          await apiClient.post<ComputationRun>(
            `/return-preparation-cases/${caseId}/computations`,
            { expected_revision: expectedRevision, regime },
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  compareRegimes: (caseId: string, expectedRevision: number) =>
    safe(
      async () =>
        (
          await apiClient.post<RegimeComparison>(
            `/return-preparation-cases/${caseId}/regime-comparisons`,
            { expected_revision: expectedRevision },
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  latestRegimeComparison: (caseId: string) =>
    safe(
      async () =>
        (
          await apiClient.get<RegimeComparison>(
            `/return-preparation-cases/${caseId}/regime-comparisons/latest`
          )
        ).data
    ),
  reviewWorkspace: (caseId: string) =>
    safe(
      async () =>
        (
          await apiClient.get<ReviewWorkspace>(
            `/return-preparation-cases/${caseId}/review-workspace`
          )
        ).data
    ),
  submitReviewSnapshot: (
    caseId: string,
    input: { expected_revision: number; selected_regime: 'OLD' | 'NEW'; maker_note: string }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<ReviewSnapshot>(
            `/return-preparation-cases/${caseId}/review-snapshots`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  acceptReviewAssignment: (
    caseId: string,
    snapshotId: string,
    input: {
      expected_revision: number
      reviewer_user_id: string
      assignment_action?: 'ASSIGNED' | 'WITHDRAWN'
      conflict_attested: true
      conflict_codes: string[]
      reason: string
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<ReviewAssignment>(
            `/return-preparation-cases/${caseId}/review-snapshots/${snapshotId}/assignments`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  decideReview: (
    caseId: string,
    snapshotId: string,
    input: {
      expected_revision: number
      displayed_snapshot_hash: string
      decision: 'RETURNED' | 'APPROVED' | 'APPROVED_WITH_EXCEPTION' | 'WITHDRAWN'
      rationale: string
      findings: Array<Record<string, unknown>>
      approved_exception_ids: string[]
      conflict_attested: boolean
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<ReviewDecision>(
            `/return-preparation-cases/${caseId}/review-snapshots/${snapshotId}/decisions`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  createReviewException: (
    caseId: string,
    snapshotId: string,
    input: {
      expected_revision: number
      exception_type: 'MISSING_DOCUMENT' | 'RECONCILIATION_DIFFERENCE'
      blocker_code: 'REQUIRED_DOCUMENT_MISSING' | 'RECONCILIATION_UNRESOLVED'
      scope_type: 'CASE' | 'SECTION' | 'DOCUMENT' | 'RECONCILIATION'
      scope_ref: string
      rationale: string
      evidence_binding_ids: string[]
      risk_level: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
      expires_at: string
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<ReviewException>(
            `/return-preparation-cases/${caseId}/review-snapshots/${snapshotId}/exceptions`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  decideReviewException: (
    caseId: string,
    exceptionId: string,
    input: {
      expected_revision: number
      decision: 'APPROVED' | 'REJECTED' | 'REVOKED'
      reason: string
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<ReviewException>(
            `/return-preparation-cases/${caseId}/review-exceptions/${exceptionId}/decisions`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  finalDeclarationSummary: (caseId: string, snapshotId: string) =>
    safe(
      async () =>
        (
          await apiClient.get<FinalDeclarationSummary>(
            `/return-preparation-cases/${caseId}/review-snapshots/${snapshotId}/final-declaration`
          )
        ).data
    ),
  signFinalDeclaration: (
    caseId: string,
    snapshotId: string,
    input: {
      expected_revision: number
      displayed_snapshot_hash: string
      displayed_summary_hash: string
      wording_version: '1.0.0'
      accepted: true
    }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<FinalDeclarationSummary>(
            `/return-preparation-cases/${caseId}/review-snapshots/${snapshotId}/final-declarations`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  evaluateReadiness: (
    caseId: string,
    input: { expected_revision: number; snapshot_id: string; displayed_snapshot_hash: string }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<ReadinessEvaluation>(
            `/return-preparation-cases/${caseId}/readiness-evaluations`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
  markReadyToFile: (
    caseId: string,
    input: { expected_revision: number; snapshot_id: string; displayed_snapshot_hash: string }
  ) =>
    safe(
      async () =>
        (
          await apiClient.post<ReadinessEvaluation>(
            `/return-preparation-cases/${caseId}:mark-ready-to-file`,
            input,
            { headers: { 'Idempotency-Key': crypto.randomUUID() } }
          )
        ).data
    ),
}
